import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getAnnotations,
  saveAnnotations,
  deleteAnnotations,
  getPredefinedRegions,
} from '@/services/annotationApi';

/**
 * Hook to fetch annotations for a video
 */
export function useAnnotations(videoId: string | undefined) {
  return useQuery({
    queryKey: ['annotations', videoId],
    queryFn: () => getAnnotations(videoId!),
    enabled: !!videoId,
    retry: false, // Don't retry if annotations don't exist
  });
}

/**
 * Hook to save annotations
 */
export function useSaveAnnotations() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: saveAnnotations,
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['annotations', variables.video_id] });
    },
  });
}

/**
 * Hook to delete annotations
 */
export function useDeleteAnnotations() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteAnnotations,
    onSuccess: (_, videoId) => {
      queryClient.invalidateQueries({ queryKey: ['annotations', videoId] });
    },
  });
}

/**
 * Hook to fetch predefined body regions
 *
 * These are curated anatomical regions that can be quickly selected
 * when creating annotations. Data is cached for 1 hour since it rarely changes.
 */
export function usePredefinedRegions() {
  return useQuery({
    queryKey: ['predefinedRegions'],
    queryFn: getPredefinedRegions,
    staleTime: 1000 * 60 * 60, // 1 hour cache
    retry: 1,
  });
}
